import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  BULK_INVITE_GUARD_NAME_REQUIRED_SENTENCE,
  BULK_INVITE_MAX_ROWS,
  BULK_INVITE_OVER_CAP_SENTENCE,
  buildBulkInvitePreview,
  sendBulkInviteBatch,
  type BulkInvitePreviewRow,
  type BulkInviteSendRowResult,
} from "../data/bulk-member-invite";
import { INVITE_ROLES, type InviteRole, type MemberInviteRow } from "../data/member-invites";
import type { MemberRow } from "../data/members";

function consoleRedirectOrigin(): string {
  if (typeof window === "undefined" || typeof window.location?.origin !== "string") {
    return "https://console.thesecops.app";
  }
  return window.location.origin;
}

function isInviteRole(value: string): value is InviteRole {
  return (INVITE_ROLES as readonly string[]).includes(value);
}

function outcomeLabel(result: BulkInviteSendRowResult): string {
  const outcome = result.outcome;
  if (outcome.status === "reserved") {
    if (outcome.authOutcome === "invite_email_sent") return "Reserved · Auth mail may be needed";
    if (outcome.authOutcome === "already_registered") return "Reserved · use Login code or password";
    if (outcome.authOutcome === "mail_budget") return "Reserved; sign-in mail not queued";
    if (outcome.authOutcome === "mail_refused") return "Reserved; sign-in mail not queued";
    if (outcome.authOutcome === "unknown") return "Reserved; mail status unknown";
    return "Reserved";
  }
  if (outcome.status === "refused") return outcome.sentence;
  if (outcome.status === "not_queued") return outcome.sentence;
  if (outcome.status === "unavailable") return outcome.sentence;
  return outcome.sentence;
}

/**
 * Platform-only bounded paste invite. Reuses invite_account_member + member-auth-invite per row.
 * No bulk resend/revoke. Guard address-only batches refuse (G-INV-02 / P-25).
 */
export function PlatformBulkInvite({
  client,
  accountId,
  canManage,
  memberRows,
  inviteRows,
  onCompleted,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  memberRows: readonly MemberRow[] | null;
  inviteRows: readonly MemberInviteRow[] | null;
  onCompleted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [rawPaste, setRawPaste] = useState("");
  const [role, setRole] = useState<InviteRole>("supervisor");
  const [phase, setPhase] = useState<"compose" | "preview" | "sending" | "done">("compose");
  const [previewRows, setPreviewRows] = useState<readonly BulkInvitePreviewRow[]>([]);
  const [overCap, setOverCap] = useState(false);
  const [results, setResults] = useState<readonly BulkInviteSendRowResult[]>([]);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [formNote, setFormNote] = useState<string | null>(null);
  const generationRef = useRef(0);
  const accountRef = useRef(accountId);
  const cancelRef = useRef(false);
  const sendingLockRef = useRef(false);
  /** Role locked into the current preview so Send cannot diverge from Preview (G-INV-02). */
  const previewRoleRef = useRef<InviteRole | null>(null);

  useEffect(() => {
    if (accountRef.current !== accountId) {
      accountRef.current = accountId;
      generationRef.current += 1;
      cancelRef.current = true;
      sendingLockRef.current = false;
      previewRoleRef.current = null;
      setOpen(false);
      setRawPaste("");
      setPhase("compose");
      setPreviewRows([]);
      setResults([]);
      setRevealedKey(null);
      setFormNote(null);
    }
  }, [accountId]);

  if (!canManage) return null;

  function resetCompose(): void {
    previewRoleRef.current = null;
    setPhase("compose");
    setPreviewRows([]);
    setResults([]);
    setRevealedKey(null);
    setFormNote(null);
    setOverCap(false);
  }

  function closePanel(): void {
    if (phase === "sending") {
      cancelRef.current = true;
    }
    setOpen(false);
    setRawPaste("");
    resetCompose();
  }

  function runPreview(): void {
    const windows = {
      members: memberRows ?? [],
      invites: inviteRows ?? [],
    };
    const built = buildBulkInvitePreview({ rawPaste, role, windows });
    previewRoleRef.current = role;
    setPreviewRows(built.rows);
    setOverCap(built.overCap);
    setPhase("preview");
    setRevealedKey(null);
    setFormNote(
      built.overCap
        ? BULK_INVITE_OVER_CAP_SENTENCE
        : built.guardRoleBlocked
          ? BULK_INVITE_GUARD_NAME_REQUIRED_SENTENCE
          : null,
    );
  }

  async function runSend(): Promise<void> {
    if (overCap || sendingLockRef.current) return;
    const lockedRole = previewRoleRef.current;
    if (lockedRole === null || lockedRole !== role) {
      setFormNote("Preview is stale. Run Preview again before sending.");
      resetCompose();
      return;
    }
    if (lockedRole === "guard") {
      setFormNote(BULK_INVITE_GUARD_NAME_REQUIRED_SENTENCE);
      return;
    }
    const eligible = previewRows.filter((row) => row.eligible);
    if (eligible.length === 0) {
      setFormNote("No eligible rows to send. Fix refused rows or change the role.");
      return;
    }
    sendingLockRef.current = true;
    cancelRef.current = false;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const accountAtStart = accountId;
    setPhase("sending");
    setResults([]);
    setFormNote(null);

    try {
      const batch = await sendBulkInviteBatch({
        client,
        accountId,
        role: lockedRole,
        rows: previewRows,
        redirectTo: `${consoleRedirectOrigin()}/`,
        generation,
        isCurrent: () =>
          generationRef.current === generation && accountRef.current === accountAtStart,
        shouldCancel: () => cancelRef.current,
        onRowSettled: (result) => {
          // Generation fence: never append ledger rows from a superseded batch into React state.
          if (generationRef.current !== generation || accountRef.current !== accountAtStart) {
            return;
          }
          setResults((current) => [...current, result]);
        },
      });

      if (generationRef.current !== generation || accountRef.current !== accountAtStart) {
        return;
      }
      setResults(batch.results);
      setPhase("done");
      setRawPaste("");
      setRevealedKey(null);
      onCompleted?.();
    } finally {
      sendingLockRef.current = false;
    }
  }

  return (
    <div className="platform-bulk-invite">
      {!open ? (
        <button
          type="button"
          className="builder__navbtn platform-bulk-invite__open"
          onClick={() => {
            setOpen(true);
            resetCompose();
          }}
        >
          Bulk invite
        </button>
      ) : (
        <section className="platform-bulk-invite__panel" aria-label="Bulk invite">
          <header className="platform-bulk-invite__header">
            <h4>Bulk invite</h4>
            <button type="button" className="builder__cancel" onClick={closePanel}>
              {phase === "sending" ? "Cancel remaining" : "Close"}
            </button>
          </header>
          <p className="platform-bulk-invite__note">
            Paste up to {BULK_INVITE_MAX_ROWS} addresses. One role for the batch. Each row uses the
            same invite path as Send invite. Conflicts shown here come from the loaded window only.
          </p>

          {phase === "compose" ? (
            <>
              <label className="platform-bulk-invite__label" htmlFor="platform-bulk-invite-paste">
                Addresses
              </label>
              <textarea
                id="platform-bulk-invite-paste"
                className="platform-bulk-invite__paste"
                value={rawPaste}
                onChange={(event) => setRawPaste(event.target.value)}
                rows={5}
                spellCheck={false}
                autoComplete="off"
                placeholder="name@example.com, other@example.com"
              />
              <label className="platform-bulk-invite__label" htmlFor="platform-bulk-invite-role">
                Role for this batch
              </label>
              <select
                id="platform-bulk-invite-role"
                value={role}
                onChange={(event) => {
                  const next = event.target.value;
                  if (isInviteRole(next)) setRole(next);
                }}
              >
                {INVITE_ROLES.map((value) => (
                  <option key={value} value={value}>
                    {value === "guard" ? "Guard" : value === "supervisor" ? "Supervisor" : "Admin"}
                  </option>
                ))}
              </select>
              {formNote !== null ? (
                <p className="platform-bulk-invite__alert" role="status">
                  {formNote}
                </p>
              ) : null}
              <div className="platform-bulk-invite__actions">
                <button
                  type="button"
                  className="minter__confirm-go"
                  onClick={runPreview}
                  disabled={rawPaste.trim().length === 0}
                >
                  Preview
                </button>
              </div>
            </>
          ) : null}

          {phase === "preview" ? (
            <>
              {formNote !== null ? (
                <p className="platform-bulk-invite__alert" role="status">
                  {formNote}
                </p>
              ) : null}
              <p className="platform-bulk-invite__note">
                Role for this batch:{" "}
                {role === "guard" ? "Guard" : role === "supervisor" ? "Supervisor" : "Admin"}. Raw
                paste is hidden until Edit paste.
              </p>
              <BulkInviteRowList
                rows={previewRows}
                revealedKey={revealedKey}
                onToggleReveal={setRevealedKey}
                mode="preview"
              />
              <div className="platform-bulk-invite__actions">
                <button type="button" className="builder__navbtn" onClick={resetCompose}>
                  Edit paste
                </button>
                <button
                  type="button"
                  className="minter__confirm-go"
                  onClick={() => void runSend()}
                  disabled={overCap || previewRows.every((row) => !row.eligible)}
                >
                  Send eligible rows
                </button>
              </div>
            </>
          ) : null}

          {phase === "sending" || phase === "done" ? (
            <>
              <p className="platform-bulk-invite__note" role="status">
                {phase === "sending"
                  ? "Sending one row at a time. Cancel stops future rows only."
                  : "Batch finished. Outcomes are per row; nothing here claims mailbox delivery."}
              </p>
              <BulkInviteRowList
                rows={results.length > 0 ? results : previewRows}
                revealedKey={revealedKey}
                onToggleReveal={setRevealedKey}
                mode={phase === "done" || results.length > 0 ? "ledger" : "preview"}
                ledger={results}
              />
            </>
          ) : null}
        </section>
      )}
    </div>
  );
}

function BulkInviteRowList({
  rows,
  revealedKey,
  onToggleReveal,
  mode,
  ledger = [],
}: {
  rows: readonly (BulkInvitePreviewRow | BulkInviteSendRowResult)[];
  revealedKey: string | null;
  onToggleReveal: (key: string | null) => void;
  mode: "preview" | "ledger";
  ledger?: readonly BulkInviteSendRowResult[];
}) {
  return (
    <ul className="platform-bulk-invite__list" aria-label={mode === "preview" ? "Preview" : "Outcomes"}>
      {rows.map((row) => {
        const key = row.normalizedEmail;
        const revealed = revealedKey === key;
        const ledgerRow = ledger.find((item) => item.normalizedEmail === key);
        const statusText =
          mode === "ledger" && ledgerRow !== undefined
            ? outcomeLabel(ledgerRow)
            : "previewFlag" in row
              ? (row.previewSentence ?? (row.eligible ? "Ready to attempt" : "Not eligible"))
              : outcomeLabel(row);

        return (
          <li key={key} className="platform-bulk-invite__row">
            <div className="platform-bulk-invite__row-main">
              <span className="platform-bulk-invite__address">
                {revealed ? row.normalizedEmail : row.maskedAddress}
              </span>
              {row.duplicateCount > 1 ? (
                <span className="platform-bulk-invite__dup">×{row.duplicateCount}</span>
              ) : null}
              <button
                type="button"
                className="platform-bulk-invite__reveal"
                aria-pressed={revealed}
                onClick={() => onToggleReveal(revealed ? null : key)}
              >
                {revealed ? "Hide" : "Reveal"}
              </button>
            </div>
            <p className="platform-bulk-invite__status">{statusText}</p>
          </li>
        );
      })}
    </ul>
  );
}
